import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { Button, Flex, Space } from 'antd';
import { type ColumnType } from 'antd/es/table';
import { useBasicDndDragger } from '../../../../core/dnd/hooks/useBasicDndDragger.ts';
import { DrawerButtonRow } from '../../../drawer/DrawerButtonRow.tsx';
import { type IPickableColumn } from '../IPickableColumn.tsx';
import { ColumnPickerItem } from './ColumnPickerItem.tsx';
import { ColumnPickerRowContainer } from './ColumnPickerRowContainer.tsx';
import { AddExtraColumnButton } from './extra-columns/AddExtraColumnButton.tsx';

export interface IColumnPickerListProps<T> {
  columns?: Array<ColumnType<T>>;
  id?: string;
  onCloseDrawer?: () => void;
  extraColumns?: Array<ColumnType<T>>;
}

function getColumn<T>(
  item: IPickableColumn,
  options: {
    columns: Array<ColumnType<T>> | null | undefined;
    extraColumns: Array<ColumnType<T>> | null | undefined;
  },
) {
  let column = options.columns?.find(f => f.key === item?.key);
  if (column) return { column, isExtra: false };
  column = options.extraColumns?.find(f => f.key === item?.key);
  if (column) return { column, isExtra: true };
  return {
    column: null,
    isExtra: false,
  };
}

export function ColumnPickerList<T>(props: IColumnPickerListProps<T>) {
  const { columns, id, onCloseDrawer, extraColumns } = props;

  const editor = useRelationEditor<IPickableColumn>('columns');

  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  if (!id || !columns?.length) return null;

  return (
    <>
      <Flex vertical gap={6}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {editor.items.map((item, index) => {
              const { column, isExtra } = getColumn(item, {
                columns,
                extraColumns,
              });

              if (!column) return null;

              return (
                <ColumnPickerRowContainer key={item.__uid} id={item.__uid}>
                  <ColumnPickerItem
                    column={column}
                    name={`columns.${index}`}
                    isExtra={isExtra}
                    editor={editor}
                  />
                </ColumnPickerRowContainer>
              );
            })}
          </SortableContext>
        </DndContext>
      </Flex>
      <div style={{ marginTop: 16 }}>
        <AddExtraColumnButton editor={editor} extraColumns={extraColumns} />
      </div>
      <DrawerButtonRow>
        <Space>
          <Button size="large" onClick={onCloseDrawer}>
            Annulla
          </Button>
          <Button size="large" htmlType="reset">
            Reset
          </Button>
          <SubmitButton />
        </Space>
      </DrawerButtonRow>
    </>
  );
}
